import React, { createContext, useContext, useState, useEffect } from 'react';
import { setSoundSettings } from '../hooks/soundEffectsManager';
import { useScreenLockAudio } from '../hooks/useScreenLockAudio';

const MusicContext = createContext();

export function MusicProvider({ children }) {
  // Handle screen lock/unlock for audio pause/resume
  useScreenLockAudio();

  // Music settings
  const [musicEnabled, setMusicEnabled] = useState(() => {
    const saved = localStorage.getItem('musicEnabled');
    return saved !== null ? saved === 'true' : true;
  });

  const [musicVolume, setMusicVolume] = useState(() => {
    const saved = localStorage.getItem('musicVolume');
    return saved !== null ? parseFloat(saved) : 0.5;
  });

  // Sound effects settings
  const [soundsEnabled, setSoundsEnabled] = useState(() => {
    const saved = localStorage.getItem('soundsEnabled');
    return saved !== null ? saved === 'true' : true;
  });

  const [soundsVolume, setSoundsVolume] = useState(() => {
    const saved = localStorage.getItem('soundsVolume');
    return saved !== null ? parseFloat(saved) : 0.5;
  });

  // Initialize sound effects manager on mount
  useEffect(() => {
    setSoundSettings(soundsEnabled, soundsVolume);
  }, []); // Only run on mount

  // Save to localStorage and sync with global manager whenever settings change
  useEffect(() => {
    localStorage.setItem('musicEnabled', musicEnabled.toString());
  }, [musicEnabled]);

  useEffect(() => {
    localStorage.setItem('musicVolume', musicVolume.toString());
  }, [musicVolume]);

  useEffect(() => {
    localStorage.setItem('soundsEnabled', soundsEnabled.toString());
    // Sync with global sound effects manager
    setSoundSettings(soundsEnabled, soundsVolume);
  }, [soundsEnabled, soundsVolume]);

  useEffect(() => {
    localStorage.setItem('soundsVolume', soundsVolume.toString());
    // Sync with global sound effects manager
    setSoundSettings(soundsEnabled, soundsVolume);
  }, [soundsVolume, soundsEnabled]);

  const toggleMusic = () => {
    setMusicEnabled(prev => !prev);
  };

  const toggleSounds = () => {
    setSoundsEnabled(prev => !prev);
  };

  const toggleAll = () => {
    // If both are enabled, disable both. Otherwise, enable both.
    const newState = !(musicEnabled && soundsEnabled);
    setMusicEnabled(newState);
    setSoundsEnabled(newState);
  };

  return (
    <MusicContext.Provider value={{ 
      musicEnabled, 
      musicVolume,
      soundsEnabled,
      soundsVolume,
      toggleMusic, 
      toggleSounds,
      toggleAll,
      setMusicVolume,
      setSoundsVolume
    }}>
      {children}
    </MusicContext.Provider>
  );
}

export function useMusic() {
  const context = useContext(MusicContext);
  if (!context) {
    throw new Error('useMusic must be used within a MusicProvider');
  }
  return context;
}

